import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { apiUrl } from '@/config/api';

const AuthContext = createContext(null);
const TOKEN_KEY = 'authToken';
const REMEMBER_FLAG_KEY = 'authRemember';
const TOKEN_COOKIE_KEY = 'authToken';
const AUTH_SYNC_KEY = 'authSync';
const REMEMBER_TRUE_VALUE = '1';
const ISOLATED_SESSION_KEY = 'isolatedAuthToken';
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 30;

const resolveCookieSecurePart = () => {
  if (typeof window === 'undefined') {
    return '';
  }

  return window.location.protocol === 'https:' ? '; Secure' : '';
};

const writeTokenCookie = (token, isPersistent = false) => {
  const encodedToken = encodeURIComponent(token);
  const securePart = resolveCookieSecurePart();
  const maxAgePart = isPersistent ? `; Max-Age=${COOKIE_MAX_AGE_SECONDS}` : '';
  document.cookie = `${TOKEN_COOKIE_KEY}=${encodedToken}; Path=/; SameSite=Lax${securePart}${maxAgePart}`;
};

const clearTokenCookie = () => {
  const securePart = resolveCookieSecurePart();
  document.cookie = `${TOKEN_COOKIE_KEY}=; Path=/; SameSite=Lax${securePart}; Max-Age=0`;
};

const readTokenCookie = () => {
  const tokenCookie = document.cookie
    .split('; ')
    .find((entry) => entry.startsWith(`${TOKEN_COOKIE_KEY}=`));

  if (!tokenCookie) {
    return null;
  }

  const tokenValue = tokenCookie.split('=').slice(1).join('=');
  return tokenValue ? decodeURIComponent(tokenValue) : null;
};

const broadcastAuthChange = () => {
  localStorage.setItem(AUTH_SYNC_KEY, String(Date.now()));
};

const readStoredAuth = () => {
  const isolatedToken = sessionStorage.getItem(ISOLATED_SESSION_KEY);
  if (isolatedToken) {
    return { token: isolatedToken, remember: false, isolated: true };
  }

  const persistentToken = localStorage.getItem(TOKEN_KEY);
  if (persistentToken) {
    return { token: persistentToken, remember: true, isolated: false };
  }

  const cookieToken = readTokenCookie();
  if (cookieToken) {
    return { token: cookieToken, remember: false, isolated: false };
  }

  const legacySessionToken = sessionStorage.getItem(TOKEN_KEY);
  if (legacySessionToken) {
    writeTokenCookie(legacySessionToken, false);
    sessionStorage.removeItem(TOKEN_KEY);
    return { token: legacySessionToken, remember: false, isolated: false };
  }

  return { token: null, remember: false, isolated: false };
};

const parseError = async (response) => {
  try {
    const data = await response.json();
    if (data?.code) {
      return data.code;
    }
    if (data?.error) {
      return data.error;
    }
  } catch (error) {
    // ignore
  }
  return 'Не удалось выполнить запрос. Попробуйте позже.';
};

const formatNetworkError = (error) => {
  if (error?.name === 'TypeError' || error?.message === 'Failed to fetch') {
    return 'Не удалось связаться с сервером. Проверьте подключение и попробуйте позже.';
  }
  return error?.message || 'Произошла ошибка при выполнении запроса.';
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [authState, setAuthState] = useState(() => readStoredAuth());
  const [initializing, setInitializing] = useState(true);
  const [bonusInfo, setBonusInfo] = useState(null);
  const { token, remember, isolated } = authState;

  const extractBonuses = useCallback((data) => {
    if (data?.bonuses && (data.bonuses.welcomeBonusApplied || data.bonuses.referralBonusApplied)) {
      return data.bonuses;
    }
    return null;
  }, []);

  useEffect(() => {
    const loadProfile = async () => {
      if (!token) {
        setInitializing(false);
        return;
      }
      try {
        const response = await fetch(apiUrl('/api/me'), {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (!response.ok) {
          throw new Error('Ошибка загрузки профиля');
        }
        const data = await response.json();
        setUser(data.user);
        setBonusInfo(null);
      } catch (error) {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(REMEMBER_FLAG_KEY);
        clearTokenCookie();
        sessionStorage.removeItem(TOKEN_KEY);
        sessionStorage.removeItem(ISOLATED_SESSION_KEY);
        setAuthState({ token: null, remember: false, isolated: false });
        setUser(null);
      } finally {
        setInitializing(false);
      }
    };
    loadProfile();
  }, [token]);

  const applyAuthData = useCallback((data, rememberSession = true, options = {}) => {
    const isolatedSession = Boolean(options.isolatedSession);

    if (isolatedSession) {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REMEMBER_FLAG_KEY);
      clearTokenCookie();
      sessionStorage.removeItem(TOKEN_KEY);
      sessionStorage.setItem(ISOLATED_SESSION_KEY, data.token);
    } else if (rememberSession) {
      sessionStorage.removeItem(ISOLATED_SESSION_KEY);
      localStorage.setItem(TOKEN_KEY, data.token);
      localStorage.setItem(REMEMBER_FLAG_KEY, REMEMBER_TRUE_VALUE);
      clearTokenCookie();
    } else {
      sessionStorage.removeItem(ISOLATED_SESSION_KEY);
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REMEMBER_FLAG_KEY);
      writeTokenCookie(data.token, false);
    }

    sessionStorage.removeItem(TOKEN_KEY);
    setAuthState({ token: data.token, remember: rememberSession, isolated: isolatedSession });
    setUser(data.user);
    setBonusInfo(extractBonuses(data));
    broadcastAuthChange();
  }, [extractBonuses]);

  const login = useCallback(async (email, password, rememberSession = false) => {
    try {
      const response = await fetch(apiUrl('/api/auth/login'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });
      if (!response.ok) {
        throw new Error(await parseError(response));
      }
      const data = await response.json();
      applyAuthData(data, rememberSession);
      return data;
    } catch (error) {
      throw new Error(formatNetworkError(error));
    }
  }, [applyAuthData]);

  const register = useCallback(async (form) => {
    try {
      const response = await fetch(apiUrl('/api/auth/register'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(form),
      });
      if (!response.ok) {
        throw new Error(await parseError(response));
      }
      const data = await response.json();
      applyAuthData(data);
      return data;
    } catch (error) {
      throw new Error(formatNetworkError(error));
    }
  }, [applyAuthData]);

  const updateProfile = useCallback(
    async (payload) => {
      if (!token) {
        throw new Error('Требуется авторизация');
      }
      const response = await fetch(apiUrl('/api/me'), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw new Error(await parseError(response));
      }
      const data = await response.json();
      setUser(data.user);
      return data.user;
    },
    [token],
  );

  const syncProfileLanguage = useCallback(
    async (languageCode, authToken) => {
      const effectiveToken = authToken || token;
      if (!effectiveToken) {
        return;
      }

      try {
        const response = await fetch(apiUrl('/api/me'), {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${effectiveToken}`,
          },
          body: JSON.stringify({ uiLanguage: languageCode }),
        });

        if (!response.ok) {
          throw new Error(await parseError(response));
        }

        const data = await response.json();
        setUser(data.user);
      } catch (error) {
        console.warn('Не удалось синхронизировать язык профиля', error);
      }
    },
    [token],
  );

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REMEMBER_FLAG_KEY);
    clearTokenCookie();
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(ISOLATED_SESSION_KEY);
    setAuthState({ token: null, remember: false, isolated: false });
    setUser(null);
    broadcastAuthChange();
  }, []);

  useEffect(() => {
    const handleStorageSync = (event) => {
      if (event.key !== AUTH_SYNC_KEY && event.key !== TOKEN_KEY && event.key !== REMEMBER_FLAG_KEY) {
        return;
      }

      const nextState = readStoredAuth();
      setAuthState((previousState) => {
        if (previousState.token === nextState.token && previousState.remember === nextState.remember) {
          return previousState;
        }
        return nextState;
      });

      if (!nextState.token) {
        setUser(null);
        setBonusInfo(null);
      }
    };

    window.addEventListener('storage', handleStorageSync);
    return () => {
      window.removeEventListener('storage', handleStorageSync);
    };
  }, []);

  const changePassword = useCallback(async (currentPassword, newPassword) => {
    if (!token) {
      throw new Error('Требуется авторизация');
    }

    try {
      const response = await fetch(apiUrl('/api/auth/change-password'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ currentPassword, newPassword }),
      });

      if (!response.ok) {
        throw new Error(await parseError(response));
      }

      const data = await response.json();
      if (data?.token && data?.user) {
        applyAuthData(data, remember, { isolatedSession: isolated });
      }

      return data;
    } catch (error) {
      throw new Error(formatNetworkError(error));
    }
  }, [applyAuthData, isolated, remember, token]);

  const value = useMemo(() => ({
    user,
    token,
    initializing,
    isAuthenticated: Boolean(user),
    applyAuthData,
    bonusInfo,
    login,
    register,
    logout,
    updateProfile,
    changePassword,
    syncProfileLanguage,
    consumeBonuses: () => setBonusInfo(null),
  }), [
    applyAuthData,
    bonusInfo,
    changePassword,
    initializing,
    login,
    logout,
    register,
    syncProfileLanguage,
    token,
    updateProfile,
    user,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth должен использоваться внутри AuthProvider');
  }
  return context;
};
